import { FC, useEffect, useRef, useState } from 'react';
import {
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    TextField,
    Typography,
    Box,
    Checkbox,
    ListItemText,
    Chip,
    Tooltip,
} from '@mui/material';
import { SCUIButton, SCUIDateRangerV2, SCUILoadingLinear, SCUINoRecordComponent } from '../../atomic/atoms';
import SCUIDownloadButton from '../../atomic/atoms/SCUIDownloadButton/SCUIDownloadButton';
import { SCUIContent } from '../../atomic/organisms';
import { SCUICustomDataGrid, SCUIBreadCrumb } from '../../atomic/molecules';
import { ExtendedGridColDef } from '@/types/ExtendedGridColDef';
import { useTranslation } from 'react-i18next';
import { format, isValid, parse, subDays } from 'date-fns';
import SearchIcon from '@mui/icons-material/Search';
import Swal from 'sweetalert2';
import { ReturnQuery } from '../../services/query';
import { ReturnCommand } from '../../services/command';
import Helper from '../../utils/helper';
import TimeHelper from '../../utils/time';

const DATE_DISPLAY_FORMAT = 'dd/MM/yyyy';
const DATE_PICKER_FORMAT = 'DD/MM/YYYY';

const getStatusChipSx = (status: string) => {
    const s = status?.toLowerCase() || '';
    if (s.includes('tamamland') || s.includes('complet'))
        return { bg: 'rgba(46,125,50,0.08)', color: '#2e7d32', border: 'rgba(46,125,50,0.25)' };
    if (s.includes('iptal') || s.includes('cancel'))
        return { bg: 'rgba(211,47,47,0.08)', color: '#d32f2f', border: 'rgba(211,47,47,0.25)' };
    if (s.includes('bekle') || s.includes('wait') || s.includes('pending'))
        return { bg: 'rgba(237,108,2,0.08)', color: '#e65100', border: 'rgba(237,108,2,0.25)' };
    return { bg: 'rgba(25,118,210,0.07)', color: '#1565c0', border: 'rgba(25,118,210,0.2)' };
};

const toApiDate = (date: string) => {
    if (!date) {
        return date;
    }

    const parsed = parse(date, DATE_DISPLAY_FORMAT, new Date());
    //
    // "returnDateStart": "2026-08-25T00:00:00.013",
    let parsedDate = isValid(parsed) ? format(parsed, 'yyyy-MM-dd') : date;
    return parsedDate + 'T00:00:00.000';
};

const extractResponseRows = (res: any): any[] => {
    if (Array.isArray(res)) {
        return res;
    }
    if (Array.isArray(res?.data)) {
        return res.data;
    }
    if (Array.isArray(res?.data?.data)) {
        return res.data.data;
    }
    if (Array.isArray(res?.items)) {
        return res.items;
    }
    return [];
};

const extractResponseCount = (res: any, rowLength: number): number => {
    if (typeof res?.count === 'number') return res.count;
    if (typeof res?.totalCount === 'number') return res.totalCount;
    if (typeof res?.data?.count === 'number') return res.data.count;
    return rowLength;
};

export const ReturnIpLogReport: FC<any> = () => {
    const { t } = useTranslation();
    const other_link: any = import.meta.env.VITE_APPURL!;
    const requestIdRef = useRef(0);

    const getPlatformLabel = (platformName?: string | null) => {
        if (!platformName) return '-';
        return t(`returnPlatformType.${platformName}`, { defaultValue: platformName });
    };

    const today = format(new Date(), DATE_DISPLAY_FORMAT);
    const oneWeekAgo = format(subDays(new Date(), 7), DATE_DISPLAY_FORMAT);
    const storeCodeInfo = Helper.getStoreCode();

    const [startDate, setStartDate] = useState<any>(oneWeekAgo);
    const [endDate, setEndDate] = useState<any>(today);
    const [rmaCode, setRmaCode] = useState<string>('');
    const [storeCode, setStoreCode] = useState<string>('');
    const [statusId, setStatusId] = useState<any[]>([]);

    const [pageSize, setPageSize] = useState(10);
    const [pageNumber, setPageNumber] = useState(0);
    const [rowCount, setRowCount] = useState(0);
    const [datas, setDatas] = useState<any[] | null>(null);
    const [loading, setLoading] = useState(false);
    const [show, setShow] = useState(0);

    const [returnStatusList, setReturnStatusList] = useState<any[]>([]);

    useEffect(() => {
        ReturnQuery.GetReturnStatus().then((res: any) => {
            if (res?.data) {
                setReturnStatusList(res.data);
            }
        });
    }, []);

    const _handlChangeDate = (event: any) => {
        setStartDate(event[0]);
        setEndDate(event[1]);
    };

    const mapToExcelRows = (data: any[]) =>
        data.map((item: any) => ({
            processDate: item.processDate ? TimeHelper.formatDateHourSecond(item.processDate) : '-',
            rmaCode: item.rmaCode != null && item.rmaCode !== '' ? String(item.rmaCode) : '-',
            orderNumber: item.orderNumber || '-',
            status: item.statusDescription || '-',
            storeCode: item.storeCode || '-',
            operatorName: item.operatorName || '-',
            platformName: getPlatformLabel(item.platformName),
            ipCreateAddress: item.ipCreateAddress || '-',
            ipCompleteAddress: item.ipCompleteAddress || '-',
            deviceCreateType: item.deviceCreateType || '-',
            deviceCompleteType: item.deviceCompleteType || '-',
        }));

    const showFetchErrorPopup = (retryFn: () => void) => {
        Swal.fire({
            icon: 'error',
            title: t('general.error'),
            text: t('returnIpLogReport.fetchError'),
            confirmButtonText: t('general.tryAgain'),
            showCancelButton: true,
            cancelButtonText: t('general.cancel'),
        }).then((result) => {
            if (result.isConfirmed) {
                retryFn();
            }
        });
    };

    const buildSearchParams = (take: number, skip: number) => {
        const params: any = {};

        if (rmaCode) {
            params.rmaCode = Number(rmaCode);
        }
        if (startDate) {
            params.returnDateStart = toApiDate(startDate);
        }
        if (endDate) {
            params.returnDateEnd = toApiDate(endDate);
        }
        if ((storeCodeInfo?.length ?? 0) > 0) {
            params.storeCode = storeCodeInfo;
        } else if (storeCode) {
            params.storeCode = storeCode;
        }
        if (statusId && statusId.length > 0) {
            params.returnStatusIds = statusId;
        }

        params.take = take;
        params.skip = skip;

        return params;
    };

    const handleExcelDownload = async (): Promise<any[]> => {
        const params = buildSearchParams(rowCount > 0 ? rowCount : pageSize, 0);

        try {
            const res = await ReturnCommand.SearchStoreReturnsWithIpLogs(params);
            const rows = extractResponseRows(res);
            return rows.length > 0 ? mapToExcelRows(rows) : [];
        } catch {
            Swal.fire({
                icon: 'error',
                title: t('general.error'),
                text: t('returnIpLogReport.fetchError'),
                confirmButtonText: t('general.ok'),
            });
            return [];
        }
    };

    const fetchIpLogReport = () => {
        const currentRequestId = ++requestIdRef.current;

        setLoading(true);

        const params = buildSearchParams(pageSize, pageNumber);

        ReturnCommand.SearchStoreReturnsWithIpLogs(params)
            .then((res: any) => {
                if (currentRequestId !== requestIdRef.current) {
                    return;
                }

                const rows = extractResponseRows(res);
                const totalCount = extractResponseCount(res, rows.length);

                if (rows.length > 0) {
                    setDatas(rows);
                    setRowCount(totalCount);
                } else {
                    setDatas([]);
                    setRowCount(0);
                }
            })
            .catch(() => {
                if (currentRequestId !== requestIdRef.current) {
                    return;
                }

                setDatas([]);
                setRowCount(0);
                showFetchErrorPopup(fetchIpLogReport);
            })
            .finally(() => {
                if (currentRequestId === requestIdRef.current) {
                    setLoading(false);
                }
            });
    };

    useEffect(() => {
        if (show === 1) {
            fetchIpLogReport();
        }
    }, [pageSize, pageNumber]);

    const DateCell = ({ value }: { value: string }) => {
        if (!value || value.trim() === '-')
            return (
                <Typography fontSize={12} color="text.secondary">
                    —
                </Typography>
            );
        const parts = value.split(' ');
        return (
            <Box>
                <Typography fontSize={12} fontWeight={600} color="text.primary" lineHeight={1.4}>
                    {parts[0]}
                </Typography>
                {parts[1] && (
                    <Typography fontSize={11} color="text.secondary" lineHeight={1.3}>
                        {parts[1]}
                    </Typography>
                )}
            </Box>
        );
    };

    const columns: ExtendedGridColDef[] = [
        {
            field: 'processDate',
            headerName: t('returnIpLogReport.processDate'),
            flex: 1,
            minWidth: 100,
            headerAlign: 'center',
            align: 'left',
            renderCell: (params: any) => (
                <DateCell value={params.value ? TimeHelper.formatDateHourSecond(params.value) : '-'} />
            ),
        },
        {
            field: 'rmaCode',
            headerName: t('return.RMACode'),
            flex: 1,
            minWidth: 100,
            renderCell: (params: any) => (
                <Typography
                    fontSize={13}
                    fontWeight={700}
                    sx={{ color: 'primary.main', cursor: 'pointer', textDecoration: 'underline' }}
                    onClick={async () => {
                        if (params.row.returnId) {
                            try {
                                setLoading(true);
                                const req = {
                                    rmaCode: parseInt(params.row.rmaCode, 10),
                                    shippingListId: 0,
                                    saleOrderId: 0,
                                    saleOrderNumber: '',
                                    shippingListNo: null,
                                };
                                const res = await ReturnCommand.PostGetReturnList(req);
                                let customerIdStr = '';
                                if (
                                    res?.returnModels &&
                                    res.returnModels.length > 0 &&
                                    res.returnModels[0].customerId
                                ) {
                                    customerIdStr = '&customerId=' + res.returnModels[0].customerId;
                                }
                                window.open(
                                    other_link + '/returnDetail/?ReturnId=' + params.row.returnId + customerIdStr,
                                    '_blank'
                                );
                            } catch (e) {
                                console.log('Hata :>> ', e);
                                window.open(other_link + '/returnDetail/?ReturnId=' + params.row.returnId, '_blank');
                            } finally {
                                setLoading(false);
                            }
                        }
                    }}
                >
                    {params.value || '-'}
                </Typography>
            ),
        },
        {
            field: 'orderNumber',
            headerName: t('general.orderNo'),
            flex: 1,
            minWidth: 110,
            renderCell: (params: any) => (
                <Typography
                    fontSize={13}
                    fontWeight={700}
                    sx={{ color: 'primary.main', cursor: 'pointer', textDecoration: 'underline' }}
                    onClick={() => {
                        if (params.value) {
                            window.open(other_link + '/orderdetail?OrderNumber=' + params.value, '_blank');
                        }
                    }}
                >
                    {params.value || '-'}
                </Typography>
            ),
        },
        {
            field: 'statusDescription',
            headerName: t('general.status'),
            flex: 1,
            minWidth: 120,
            renderCell: (params: any) => {
                const statusStr = params.value || '-';
                const c = getStatusChipSx(statusStr);
                return (
                    <Tooltip title={statusStr} placement="top" arrow>
                        <Chip
                            label={statusStr}
                            size="small"
                            sx={{
                                fontSize: 11,
                                fontWeight: 600,
                                height: 22,
                                backgroundColor: c.bg,
                                color: c.color,
                                border: `1px solid ${c.border}`,
                                maxWidth: 120,
                            }}
                        />
                    </Tooltip>
                );
            },
        },
        {
            field: 'storeCode',
            headerName: t('general.storeCode'),
            flex: 1,
            minWidth: 90,
            renderCell: (params: any) => <Typography fontSize={13}>{params.value || '-'}</Typography>,
        },
        {
            field: 'operatorName',
            headerName: t('returnIpLogReport.operatorName'),
            flex: 1,
            minWidth: 110,
            renderCell: (params: any) => <Typography fontSize={13}>{params.value || '-'}</Typography>,
        },
        {
            field: 'platformName',
            headerName: t('returnIpLogReport.platformName'),
            flex: 1,
            minWidth: 100,
            renderCell: (params: any) => <Typography fontSize={13}>{getPlatformLabel(params.value)}</Typography>,
        },
        {
            field: 'ipCreateAddress',
            headerName: t('returnIpLogReport.ipCreateAddress'),
            flex: 1,
            minWidth: 130,
            renderCell: (params: any) => <Typography fontSize={13}>{params.value || '-'}</Typography>,
        },
        {
            field: 'ipCompleteAddress',
            headerName: t('returnIpLogReport.ipCompleteAddress'),
            flex: 1,
            minWidth: 130,
            renderCell: (params: any) => <Typography fontSize={13}>{params.value || '-'}</Typography>,
        },
        {
            field: 'deviceCreateType',
            headerName: t('returnIpLogReport.deviceCreateType'),
            flex: 1,
            minWidth: 130,
            renderCell: (params: any) => <Typography fontSize={13}>{params.value || '-'}</Typography>,
        },
        {
            field: 'deviceCompleteType',
            headerName: t('returnIpLogReport.deviceCompleteType'),
            flex: 1,
            minWidth: 130,
            renderCell: (params: any) => <Typography fontSize={13}>{params.value || '-'}</Typography>,
        },
    ];

    const hasResults = Array.isArray(datas) && datas.length > 0;
    const hasEmptyResult = show === 1 && !loading && Array.isArray(datas) && datas.length === 0;

    return (
        <SCUIContent>
            <Box sx={{ mb: 2 }}>
                <SCUIBreadCrumb title={t('dashboard.returnIpLogReport')} componentLink={'returnIpLogReport'} />
            </Box>

            <Box sx={{ position: 'relative', minHeight: 4 }}>
                <SCUILoadingLinear display={loading} />
            </Box>

            <Paper
                elevation={0}
                sx={{
                    border: '1px solid',
                    borderColor: 'grey.200',
                    borderRadius: 3,
                    px: 2.5,
                    py: 2,
                    mb: 2,
                    boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
                }}
            >
                <Grid container spacing={2} alignItems="center">
                    <Grid size={{ md: 3, xs: 12 }}>
                        <SCUIDateRangerV2
                            size="small"
                            setDateRange={_handlChangeDate}
                            dateFormat={DATE_PICKER_FORMAT}
                            fromDate={startDate}
                            toDate={endDate}
                        />
                    </Grid>

                    <Grid size={{ md: 2, xs: 12 }}>
                        <TextField
                            fullWidth
                            size="small"
                            label={t('general.customerReturnCode')}
                            value={rmaCode}
                            onChange={(e) => {
                                const val = e.target.value;
                                if (/^\d*$/.test(val)) {
                                    setRmaCode(val);
                                }
                            }}
                            placeholder={t('general.customerReturnCode')}
                        />
                    </Grid>

                    <Grid size={{ md: 3, xs: 12 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>{t('comments.selectStatus')}</InputLabel>
                            <Select
                                multiple
                                value={statusId}
                                label={t('comments.selectStatus')}
                                onChange={(e) => setStatusId(e.target.value as any[])}
                                renderValue={(selected) => {
                                    if (selected.length === 0) return '';
                                    return returnStatusList
                                        ?.filter((item: any) => selected.includes(item.returnStatusId))
                                        .map((item: any) => item.statusDescription)
                                        .join(', ');
                                }}
                            >
                                {returnStatusList?.map((item: any) => (
                                    <MenuItem key={item.returnStatusId} value={item.returnStatusId}>
                                        <Checkbox checked={statusId.indexOf(item.returnStatusId) > -1} />
                                        <ListItemText primary={item.statusDescription} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid size={{ md: 2, xs: 12 }}>
                        {!storeCodeInfo || storeCodeInfo.length === 0 ? (
                            <TextField
                                fullWidth
                                size="small"
                                label={t('general.storeCode')}
                                value={storeCode}
                                onChange={(e) => setStoreCode(e.target.value.trim())}
                                placeholder={t('general.storeCode')}
                            />
                        ) : (
                            <TextField
                                fullWidth
                                size="small"
                                label={t('general.storeCode')}
                                value={storeCodeInfo}
                                disabled
                            />
                        )}
                    </Grid>

                    <Grid size={{ md: 2, xs: 12 }}>
                        <SCUIButton
                            text={t('general.filter')}
                            type="primary"
                            sizeType="fixed"
                            size="m"
                            showStartIcon
                            startIcon={<SearchIcon style={{ fontSize: '15px' }} />}
                            onClick={() => {
                                setPageSize(10);
                                setPageNumber(0);
                                fetchIpLogReport();
                                setShow(1);
                            }}
                            style={{ height: '40px', width: '100%' }}
                        />
                    </Grid>
                </Grid>
            </Paper>

            {hasEmptyResult && <SCUINoRecordComponent text="returnIpLogReport.noRecordText" />}

            {datas && show === 1 && hasResults && (
                <Box>
                    <SCUIDownloadButton
                        position="right"
                        excelData={{
                            excelDownloadData: [],
                            excelDownloadFolderName: 'ReturnIpLogReport',
                            excelDownloadButtonText: t('general.exportToExcel'),
                        }}
                        onclick={handleExcelDownload}
                    />
                    <SCUICustomDataGrid
                        randomId
                        rows={datas}
                        columns={columns}
                        onPageSizeChange={(i: number) => {
                            setPageSize(i);
                            setPageNumber(0);
                        }}
                        pageSize={pageSize}
                        paginationModeChange
                        onPageChange={(i: number) => {
                            setPageNumber(i);
                        }}
                        page={pageNumber}
                        rowCount={rowCount}
                        sx={{
                            border: 0,
                            '& .MuiDataGrid-columnHeaders': {
                                backgroundColor: 'grey.50',
                                fontWeight: 700,
                                fontSize: 12,
                            },
                        }}
                    />
                </Box>
            )}
        </SCUIContent>
    );
};
